import { Box } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles';
import { formatDateTimeReadable, formatMoney, LoanCriteria } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';
import { generatePath } from 'react-router-dom';

import { getCardHeaderParamsByApplicationType } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/config';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { SizedIconWithCircle } from '@/components/shared/components/SizedIconWithCircle';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { LinkToDetailButton } from '@/components/shared/components/SuperSearchBar/components/SearchResultCard/components/shared/LinkToDetailButton';
import { SearchBarFilterOptions } from '@/components/shared/components/SuperSearchBar/types/SearchBarFilterOptions';
import { Text } from '@/components/shared/components/Text';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { DETAILS_PATH_BY_PRODUCT } from '@/config/config';
import { ApplicationType, StatusTagType } from '@/config/types';
import { STYLE_PARAMS_BY_STATUS } from '@/constants/styleParamsByStatus';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { getApplicationTypeByLoanCriteria } from '@/utils/getApplicationTypeByLoanCriteria';
import { amountToValueMoneyMapper } from '@/utils/mappers/amountToValueMoneyMapper';

interface ApplicationSearchResultCardProps {
  classes: ClassNameMap<'root'>;
  selected: boolean;
  searchResult: LoanCriteria;
  toggleDialog: () => void;
  searchBarState: SearchBarFilterOptions;
  onNavigate?: boolean;
}

export const ApplicationSearchResultCard = ({
  classes,
  selected,
  searchResult,
  toggleDialog,
  searchBarState,
  onNavigate,
}: ApplicationSearchResultCardProps) => {
  const { palette, customBorderRadius } = useCustomTheme();
  const applicationType = getApplicationTypeByLoanCriteria(searchResult);

  const getValuesByResultType = () => {
    if ('assetType' in searchResult) {
      return {
        borrowerFullName: `${searchResult.borrowerInvitation.fullName.firstName} ${searchResult.borrowerInvitation.fullName.lastName}`,
        principal: searchResult.loanConditions?.principal,
        status: searchResult.status,
        iconName: searchResult?.assetType ? getCardHeaderParamsByApplicationType(applicationType).iconName : 'sell',
        applicationType: searchResult.assetType ? applicationType : ApplicationType.CONSUMER_LOAN,
      };
    }
  };
  const displayValues = getValuesByResultType();

  const isCard = displayValues?.applicationType === ApplicationType.CARD;
  const isCardWithLoan = isCard && !('isLoan' in searchResult);

  const targetPath =
    !displayValues || isCardWithLoan
      ? DYNAMIC_PAGE_PATHS.CONSUMER_LOAN
      : DETAILS_PATH_BY_PRODUCT[
          displayValues.applicationType as
            | ApplicationType.AUTO
            | ApplicationType.CONSUMER_LOAN
            | ApplicationType.CREDIT_LINE
            | ApplicationType.ORDER
            | ApplicationType.PAY_BY_LINK
        ];

  const onClick = () => {
    window.location.replace(
      generatePath(targetPath, {
        loanId: searchResult.id,
      }),
    );
    toggleDialog();
  };

  useEffect(() => {
    if (!selected || !onNavigate) return;
    onClick();
  }, [onNavigate]);

  if (!displayValues) {
    return null;
  }

  return (
    <Box className={'appear-anim'}>
      <CustomPaper
        fill={selected ? 'grey.dark' : 'grey.light'}
        style={{ padding: 8, cursor: 'pointer' }}
        className={classes.root}
        onClick={onClick}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box className={'overflow-text-ellipsis'} display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <SizedIconWithCircle
              copyToClipboard={{
                value: searchResult.id,
                textId: 'COPY_ID',
              }}
              variant={'medium'}
              iconColor={palette.secondary.main}
              backgroundColor={'transparent'}
              customBorderRadius={customBorderRadius.button}
              iconName={displayValues?.iconName}
            />
            <Box display={'flex'} flexDirection={'column'} pl={1} className={'overflow-text-ellipsis'}>
              <Font variant={'body2'} color={'title'} className={'overflow-text-ellipsis'}>
                {!isCard ? (
                  <Text
                    strong
                    tId={'DYNAMIC_CUSTOMER_NAME_WITH_APPLICATION_AMOUNT'}
                    dynamicDataTexts={[
                      displayValues.borrowerFullName,
                      formatMoney(amountToValueMoneyMapper(displayValues.principal)),
                    ]}
                  />
                ) : (
                  <strong>{displayValues.borrowerFullName}</strong>
                )}
              </Font>
              <Font
                variant={'caption'}
                color={'textSecondary'}
                className={'overflow-text-ellipsis'}
              >{`${formatDateTimeReadable(searchResult[searchBarState.dateToShow])} | ${
                searchResult.businessName
              }`}</Font>
            </Box>
          </Box>
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'} ml={2}>
            <StatusTag
              labelTextId={STYLE_PARAMS_BY_STATUS[displayValues?.status]?.title}
              type={STYLE_PARAMS_BY_STATUS[displayValues?.status]?.colorPaletteName ?? StatusTagType.DEFAULT}
              colorType={STYLE_PARAMS_BY_STATUS[displayValues?.status]?.colorType ?? StatusTagType.DEFAULT}
            />
            <LinkToDetailButton onClick={onClick} />
          </Box>
        </Box>
      </CustomPaper>
    </Box>
  );
};
